import { useSyncExternalStore } from "react";

/**
 * 에피소드 영상 재생 속도 선호. 사용자 단위 전역 값이고 브라우저에 저장한다.
 *
 * 두 값을 함께 저장한다.
 * - **manual**: 사용자가 직접 골랐는가.
 * - **rate**: 그 배속.
 * `manual: false` 는 "아직 바꾸지 않았다"는 뜻이다. 그래서 모드 기본 배속(리뷰 5x · 뷰 1x)이
 * 적용된다. 한 번 `setManual` 로 저장하면 그 뒤에는 모드를 바꿔도 배속을 덮어쓰지 않는다. 그
 * 값을 사용자의 의도로 간주한다.
 *
 * 값을 localStorage 에 저장해 새로고침·새 탭에서도 유지한다. 저장소 접근이 막힌 환경
 * (사파리 private, storage 차단)에서는 메모리 값만 쓰고 조용히 degrade 한다.
 */

export type EpisodePlaybackRatePref = {
  manual: boolean;
  rate: number;
};

const STORAGE_KEY = "hatchery-registry:episode-playback-rate";

// 기본값. 아직 사용자가 배속을 고르지 않았다. manual 이 아니므로 모드 기본 배속이 적용된다.
const DEFAULT_PREF: EpisodePlaybackRatePref = { manual: false, rate: 1 };

const listeners = new Set<() => void>();
let pref = readStored();

export const episodePlaybackRateStore = {
  subscribe(listener: () => void): () => void {
    listeners.add(listener);
    return () => {
      listeners.delete(listener);
    };
  },
  pref(): EpisodePlaybackRatePref {
    return pref;
  },
  /** 사용자가 배속을 직접 골랐다. 그 뒤부터는 이 값이 manual set 으로 남는다. */
  setManual(rate: number): void {
    if (pref.manual && pref.rate === rate) {
      return;
    }
    pref = { manual: true, rate };
    writeStored(pref);
    for (const listener of listeners) {
      listener();
    }
  },
};

/** 에피소드 재생 속도 선호. 영상 플레이어(리뷰·분석 탭)가 이 값을 구독한다. */
export function useEpisodePlaybackRate(): EpisodePlaybackRatePref {
  return useSyncExternalStore(episodePlaybackRateStore.subscribe, episodePlaybackRateStore.pref);
}

function readStored(): EpisodePlaybackRatePref {
  if (typeof window === "undefined") {
    return DEFAULT_PREF;
  }
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (raw === null) {
      return DEFAULT_PREF;
    }
    const parsed: unknown = JSON.parse(raw);
    if (isValidPref(parsed)) {
      return parsed;
    }
    return DEFAULT_PREF;
  } catch {
    return DEFAULT_PREF;
  }
}

function isValidPref(value: unknown): value is EpisodePlaybackRatePref {
  if (typeof value !== "object" || value === null) {
    return false;
  }
  const candidate = value as Record<string, unknown>;
  return (
    typeof candidate.manual === "boolean" &&
    typeof candidate.rate === "number" &&
    Number.isFinite(candidate.rate) &&
    candidate.rate > 0
  );
}

function writeStored(next: EpisodePlaybackRatePref): void {
  if (typeof window === "undefined") {
    return;
  }
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
  } catch {
    // 저장이 실패해도 이번 세션의 메모리 값만 잃는다. 기능을 막지 않는다.
  }
}
